import AnalyticsLogger, { type InvestigationEvidenceSource } from 'models/AnalyticsLogger';
import type {
  InvestigationEventKind,
  InvestigationVisibility,
} from 'src/hooks/investigations/useInvestigations';

export interface EvidenceSelection {
  event: ReadonlySet<string>;
  clip: ReadonlySet<string>;
}

/** Omitting a kind means that kind was not saved, so it contributes no counts. */
export type PersistedEvidence = Partial<EvidenceSelection>;

export interface EvidenceDelta {
  eventsAdded: number;
  eventsRemoved: number;
  clipsAdded: number;
  clipsRemoved: number;
}

export interface InvestigationUpdateChanges {
  titleChanged: boolean;
  descriptionChanged: boolean;
  visibilityChanged: boolean;
}

interface InvestigationFields {
  title: string;
  description?: string | null;
  visibility: InvestigationVisibility;
}

/** Single-item add. Uses the mobile event name. clipId is included only for a clip. */
export function trackInvestigationItemAdded({
  kind,
  itemId,
  investigationId,
  isNewInvestigation,
}: {
  kind: InvestigationEventKind;
  itemId: string;
  investigationId: string;
  isNewInvestigation: boolean;
}): void {
  AnalyticsLogger.clipInvestigationLinked({
    ...(kind === 'clip' ? { clipId: itemId } : {}),
    investigationId,
    isNewInvestigation,
  });
}

/** Bulk picker save. Sends aggregate counts and the investigation id. Skips a zero-delta save. */
export function trackInvestigationEvidenceUpdate({
  investigationId,
  source,
  isNewInvestigation,
  original,
  persisted,
}: {
  investigationId: string;
  source: InvestigationEvidenceSource;
  isNewInvestigation: boolean;
  original: EvidenceSelection;
  persisted: PersistedEvidence;
}): void {
  const delta = investigationEvidenceDelta(original, persisted);
  if (!delta) return;
  AnalyticsLogger.investigationEvidenceUpdated({
    investigationId,
    source,
    isNewInvestigation,
    ...delta,
  });
}

/** Counts added/removed ids. Returns null when the save changed nothing. */
export function investigationEvidenceDelta(
  original: EvidenceSelection,
  persisted: PersistedEvidence,
): EvidenceDelta | null {
  const events = kindDelta(original.event, persisted.event);
  const clips = kindDelta(original.clip, persisted.clip);
  const delta: EvidenceDelta = {
    eventsAdded: events.added,
    eventsRemoved: events.removed,
    clipsAdded: clips.added,
    clipsRemoved: clips.removed,
  };
  const total = delta.eventsAdded + delta.eventsRemoved + delta.clipsAdded + delta.clipsRemoved;
  return total === 0 ? null : delta;
}

function kindDelta(
  original: ReadonlySet<string>,
  persisted: ReadonlySet<string> | undefined,
): { added: number; removed: number } {
  if (!persisted) return { added: 0, removed: 0 };

  let added = 0;
  let removed = 0;
  for (const id of persisted) {
    if (!original.has(id)) added += 1;
  }
  for (const id of original) {
    if (!persisted.has(id)) removed += 1;
  }
  return { added, removed };
}

/** Changed-field flags only. Returns null when the save did not change metadata. */
export function investigationUpdateChanges(
  before: InvestigationFields,
  after: InvestigationFields,
): InvestigationUpdateChanges | null {
  const changes: InvestigationUpdateChanges = {
    titleChanged: before.title.trim() !== after.title.trim(),
    descriptionChanged:
      normalizedDescription(before.description) !== normalizedDescription(after.description),
    visibilityChanged: before.visibility !== after.visibility,
  };
  if (!changes.titleChanged && !changes.descriptionChanged && !changes.visibilityChanged) {
    return null;
  }
  return changes;
}

function normalizedDescription(description: string | null | undefined): string {
  return description?.trim() ?? '';
}
